import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { User, Save, Calendar, Edit2, MapPin, LogOut, Home } from "lucide-react";
import { toast } from "react-toastify";

const CampusProfile = ({ currentUserId, visitedUserId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    bio: "",
    avatar_url: "",
    campus_alias: "",
    created_at: "",
  });
  const [editMode, setEditMode] = useState(false);
  const [newBio, setNewBio] = useState("");

  // Avatar State
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = React.useRef(null);

  // My Posts State
  const [myPosts, setMyPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  // Determine if we are viewing our own profile
  const isOwnProfile = currentUserId === visitedUserId;

  useEffect(() => {
    fetchProfile();
    fetchMyPosts();
  }, [visitedUserId]);

  const fetchProfile = async () => {
    if (!visitedUserId) return;
    const { data, error } = await supabase
      .from("profiles")
      .select("*, campuses(name)")
      .eq("id", visitedUserId)
      .single();

    if (error) {
      console.error("Error fetching profile:", error);
      toast.error("Could not load profile.");
    } else {
      setProfile(data);
      setNewBio(data.bio || "");
    }
    setLoading(false);
  };

  const fetchMyPosts = async () => {
    if (!visitedUserId) return;
    setLoadingPosts(true);
    const { data, error } = await supabase
      .from("posts")
      .select("*")
      .eq("author_id", visitedUserId)
      .order("created_at", { ascending: false });

    if (error) console.error("Error fetching posts:", error);
    else setMyPosts(data || []);
    setLoadingPosts(false);
  };

  const handleVisitHome = () => {
    localStorage.setItem('cuconnect_manual_exit', 'true');
    navigate('/welcome');
  };

  const handleLogout = async () => {
    localStorage.removeItem('cuconnect_manual_exit');
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error("Error signing out.");
    } else {
      toast.success("Session removed.");
      // Reload to reset app state
      window.location.reload();
    }
  };

  const handleSaveBio = async () => {
    if (!isOwnProfile) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ bio: newBio })
      .eq("id", currentUserId);

    if (error) {
      toast.error("Failed to update profile.");
    } else {
      setProfile((prev) => ({ ...prev, bio: newBio }));
      setEditMode(false);
      toast.success("Profile updated!");
    }
    setSaving(false);
  };

  const handleAvatarSelect = async (e) => {
    if (!isOwnProfile) return;
    const file = e.target.files[0];
    if (!file) return;

    // 2MB Limit
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be less than 2MB");
      return;
    }

    setUploadingAvatar(true);
    const fileExt = file.name.split(".").pop();
    const fileName = `avatar_${currentUserId}_${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    // Upload to 'post-images' bucket (reusing it for now, organized in folders)
    const { error: uploadError } = await supabase.storage
      .from("post-images")
      .upload(filePath, file);

    if (uploadError) {
      toast.error("Failed to upload avatar.");
      setUploadingAvatar(false);
      return;
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from("post-images").getPublicUrl(filePath);

    // Update Profile
    const { error: updateError } = await supabase
      .from("profiles")
      .update({ avatar_url: publicUrl })
      .eq("id", currentUserId);

    if (updateError) {
      toast.error("Failed to save avatar.");
    } else {
      setProfile((prev) => ({ ...prev, avatar_url: publicUrl }));
      toast.success("Avatar updated!");
    }
    setUploadingAvatar(false);
  };

  const handleDeletePost = async (postId) => {
    if (!isOwnProfile) return;
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    const { error } = await supabase
      .from("posts")
      .delete()
      .eq("id", postId)
      .eq("author_id", currentUserId); // Double check ownership

    if (error) {
      toast.error("Failed to delete post.");
    } else {
      setMyPosts((prev) => prev.filter((p) => p.id !== postId));
      toast.success("Post deleted.");
    }
  };

  if (loading)
    return (
      <div className="text-center py-12 text-gray-500 dark:text-slate-400">
        Loading profile...
      </div>
    );

  return (
    <div className="max-w-4xl mx-auto pb-16 sm:pb-20 space-y-6 sm:space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Profile Header - Premium & Elevated */}
      <div className="glass-card premium-shadow p-6 sm:p-8 flex flex-col items-center text-center relative overflow-hidden transition-all">
        <div className="absolute top-0 left-0 w-full h-24 sm:h-32 bg-gradient-to-br from-indigo-500/10 via-purple-500/5 to-transparent dark:from-indigo-600/20 dark:via-purple-600/10 dark:to-transparent z-0"></div>

        <div className="relative z-10 mt-4 sm:mt-6 mb-4 sm:mb-6 group">
          <div
            className={`w-32 h-32 sm:w-36 sm:h-36 rounded-[2rem] sm:rounded-[2.5rem] border-3 sm:border-4 border-white dark:border-[#0a0e27] shadow-xl bg-white dark:bg-gradient-to-b dark:from-[#0f1535] dark:to-[#0a0e27] flex items-center justify-center overflow-hidden relative transition-all duration-500 group-hover:scale-105 group-hover:rotate-3 ${
              isOwnProfile ? "cursor-pointer" : ""
            }`}
          >
            {profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt="Avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-4xl sm:text-5xl">🎓</div>
            )}

            {isOwnProfile && (
              <div
                className="absolute inset-0 bg-indigo-600/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center backdrop-blur-sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <span className="text-white text-[10px] sm:text-xs font-black uppercase tracking-widest">
                  Update
                </span>
              </div>
            )}

            {uploadingAvatar && (
              <div className="absolute inset-0 bg-white/90 dark:bg-[#0f1535]/90 flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-indigo-500"></div>
              </div>
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleAvatarSelect}
            accept="image/*"
            className="hidden"
          />
        </div>

        <div className="relative z-10 space-y-1.5 sm:space-y-2">
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {profile.campus_alias || "Unknown User"}
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            <p className="text-[9px] sm:text-[10px] text-indigo-600 dark:text-indigo-300 flex items-center gap-1 sm:gap-1.5 font-black uppercase tracking-[0.2em] bg-indigo-50 dark:bg-indigo-500/20 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl">
              <MapPin size={11} className="sm:w-3 sm:h-3" />
              {profile.campuses?.name ||
                profile.campus_code ||
                "Unknown Campus"}
            </p>
            <p className="text-[9px] sm:text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-1 sm:gap-1.5 font-black uppercase tracking-[0.2em] bg-slate-50 dark:bg-white/5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl">
              <Calendar size={12} />
              Joined{" "}
              {profile.created_at
                ? new Date(profile.created_at).toLocaleDateString()
                : "Recently"}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        {isOwnProfile && (
          <div className="relative z-10 flex flex-wrap justify-center gap-3 mt-8">
            <button
              onClick={handleVisitHome}
              className="flex items-center gap-2 px-6 py-2.5 text-[10px] font-black text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40 rounded-xl hover:bg-indigo-100 transition-all uppercase tracking-widest"
            >
              <Home size={14} />
              VISIT HOME
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-2.5 text-[10px] font-black text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/40 rounded-xl hover:bg-red-100 transition-all uppercase tracking-widest"
            >
              <LogOut size={14} />
              SIGN OUT
            </button>
          </div>
        )}

        {/* Bio Section */}
        <div className="relative z-10 mt-6 sm:mt-10 w-full max-w-md text-left bg-slate-50 dark:bg-white/5 dark:backdrop-blur-sm p-4 sm:p-6 rounded-[2rem] border border-slate-100 dark:border-indigo-500/20">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <h3 className="text-[9px] sm:text-[10px] font-black text-slate-400 dark:text-slate-400 uppercase tracking-widest">
              About Mentee
            </h3>
            {isOwnProfile && !editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="text-indigo-600 dark:text-indigo-300 text-[9px] sm:text-[10px] font-black uppercase tracking-widest hover:underline"
              >
                Edit
              </button>
            )}
          </div>

          {editMode ? (
            <div className="space-y-2.5 sm:space-y-3">
              <textarea
                value={newBio}
                onChange={(e) => setNewBio(e.target.value)}
                className="w-full p-3 sm:p-4 text-xs sm:text-sm border-none bg-white dark:bg-white/10 dark:backdrop-blur-sm text-slate-900 dark:text-white rounded-2xl focus:ring-4 focus:ring-indigo-500/20 outline-none resize-none"
                rows={3}
                maxLength={200}
              />
              <div className="flex justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => setEditMode(false)}
                  className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-slate-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBio}
                  disabled={saving}
                  className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest bg-indigo-600 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-xl hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-slate-700 dark:text-slate-300 text-xs sm:text-sm font-medium leading-relaxed italic">
              {profile.bio || "Crafting a unique story..."}
            </p>
          )}
        </div>
      </div>

      {/* Stats Cards - Sleek Grid */}
      <div className="grid grid-cols-2 gap-6">
        <div className="glass-card p-6 border border-slate-100 dark:border-indigo-500/20 dark:bg-white/5 dark:backdrop-blur-sm text-center group">
          <div className="text-3xl font-black text-indigo-600 dark:text-indigo-300 transition-transform group-hover:scale-110 duration-500">
            {myPosts.length}
          </div>
          <div className="text-[10px] text-slate-400 dark:text-slate-400 font-black uppercase tracking-widest mt-1">
            Total Posts
          </div>
        </div>
        <div className="glass-card p-6 border border-slate-100 dark:border-indigo-500/20 dark:bg-white/5 dark:backdrop-blur-sm text-center grayscale opacity-50 group">
          <div className="text-3xl font-black text-slate-400 dark:text-slate-500 group-hover:scale-110 transition-transform duration-500">
            0
          </div>
          <div className="text-[10px] text-slate-400 dark:text-slate-400 font-black uppercase tracking-widest mt-1">
            Karma Points
          </div>
        </div>
      </div>

      {/* My Activity - Refined List */}
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between px-2">
          <h3 className="font-black text-slate-900 dark:text-white text-base sm:text-lg tracking-tight uppercase tracking-wider">
            {isOwnProfile ? "My Pipeline" : "Recent Activity"}
          </h3>
          <div className="h-px flex-1 bg-slate-100 dark:bg-indigo-500/10 mx-4 sm:mx-6"></div>
        </div>

        {loadingPosts ? (
          <div className="text-center py-8 sm:py-12 text-slate-400 dark:text-slate-400 font-bold uppercase text-[9px] sm:text-[10px] tracking-widest">
            Syncing activity...
          </div>
        ) : myPosts.length === 0 ? (
          <div className="text-center py-8 sm:py-12 glass-card border-dashed border-slate-200 dark:border-indigo-500/20 dark:bg-white/5 dark:backdrop-blur-sm">
            <p className="text-slate-400 dark:text-slate-400 text-[10px] sm:text-xs font-bold uppercase tracking-widest">
              No recent engagements found.
            </p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {myPosts.map((post) => (
              <div
                key={post.id}
                className="glass-card p-4 sm:p-5 flex gap-4 sm:gap-6 items-center group hover:bg-slate-50 dark:hover:bg-white/10 dark:border-indigo-500/20 dark:bg-white/5 dark:backdrop-blur-sm transition-all"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-[1rem] sm:rounded-[1.2rem] overflow-hidden bg-slate-100 dark:bg-indigo-500/20 shrink-0 border border-slate-100 dark:border-indigo-500/20">
                  {post.image_url ? (
                    <img
                      src={post.image_url}
                      alt="Post"
                      className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300 dark:text-slate-500">
                      <User size={20} className="sm:w-6 sm:h-6" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 font-medium line-clamp-1 mb-1.5 sm:mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-300 transition-colors">
                    {post.content}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-[8px] sm:text-[10px] text-slate-400 dark:text-slate-400 font-bold uppercase tracking-widest">
                      {new Date(post.created_at).toLocaleDateString()}
                    </span>
                    {isOwnProfile && (
                      <button
                        onClick={() => handleDeletePost(post.id)}
                        className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-red-400 hover:text-red-600 dark:hover:text-red-400 px-2 sm:px-3 py-1 sm:py-1.5 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-all"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampusProfile;
